import React, { useEffect, useState } from "react";
import {
  Box, Button, VStack, HStack, Text, Heading, useColorModeValue,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  Tooltip, SimpleGrid, Icon, useBreakpointValue,
} from "@chakra-ui/react";
import { Document, Page, pdfjs } from "react-pdf";
import axios from "axios";
import { FaClipboardList } from "react-icons/fa";
import { StudentBatchAssignmentTree, StudentAssignmentNode, StudentInfo } from "../Common/types";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

const StudentAssignments = () => {
  const [currentStudent, setCurrentStudent] = useState<StudentInfo | null>(null);
  const [tree, setTree] = useState<StudentBatchAssignmentTree[]>([]);
  const [selectedAssignment, setSelectedAssignment] = useState<StudentAssignmentNode | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const panelBg = useColorModeValue("gray.50", "gray.800");
  const hoverBg = useColorModeValue("gray.100", "gray.700");
  const mutedText = useColorModeValue("gray.500", "gray.400");

  useEffect(() => {
    const fetchStudent = async () => {
      try {
        const apiurl = process.env.REACT_APP_API_URL || "https://default-url.com";
        const response = await axios.get(`${apiurl}/students-info`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setCurrentStudent(response.data);
      } catch (error) {
        console.error("Error fetching student info:", error);
      }
    };
    fetchStudent();
  }, []);

  useEffect(() => {
    const fetchTree = async () => {
      if (!currentStudent) return;
      try {
        const apiurl = process.env.REACT_APP_API_URL || "https://default-url.com";
        const response = await axios.get(`${apiurl}/students/${currentStudent.student_id}/batch-assignment-tree`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setTree(response.data);
      } catch (error) {
        console.error("Error fetching assignment tree:", error);
      }
    };
    fetchTree();
  }, [currentStudent]);

  // Reset the viewer whenever the selection changes — clears whatever PDF
  // was previously loaded so it can't silently persist behind a new selection.
  useEffect(() => {
    setPdfUrl((prevUrl) => {
      if (prevUrl) URL.revokeObjectURL(prevUrl);
      return null;
    });
    setNumPages(null);
  }, [selectedAssignment?.id]);

  const handleSelectAssignment = async (assignment: StudentAssignmentNode) => {
    if (!assignment.assignment_url) return;
    setSelectedAssignment(assignment);
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "https://default-url.com";
      const response = await fetch(`${apiUrl}/stream-assignment-content/${assignment.id}/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!response.ok) throw new Error("Failed to fetch assignment PDF");
      const pdfBlob = await response.blob();
      setPdfUrl(URL.createObjectURL(pdfBlob));
    } catch (error) {
      console.error("Error fetching assignment PDF:", error);
    } finally {
      setLoading(false);
    }
  };

  const isStacked = useBreakpointValue({ base: true, lg: false });

  return (
    <Box as="main" mx="auto" maxWidth="1400px" p={4}>
      <Heading size="md" mb={4}>My Assignments</Heading>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <Box bg={panelBg} borderRadius="lg" p={3} maxH="75vh" overflowY="auto">
          {tree.length === 0 && (
            <Text fontSize="sm" color={mutedText} p={2}>No assignments available yet.</Text>
          )}
          <Accordion allowMultiple defaultIndex={[0]}>
            {tree.map((batch) => (
              <AccordionItem key={batch.batch_id} border="none" mb={1}>
                <AccordionButton borderRadius="lg" _hover={{ bg: hoverBg }} px={3}>
                  <Box flex="1" textAlign="left">
                    <Text fontSize="sm" fontWeight="bold" noOfLines={1}>{batch.batch_title}</Text>
                    <Text fontSize="11px" color={mutedText} noOfLines={1}>{batch.course_title}</Text>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pt={2} pb={1} px={1}>
                  <Accordion allowMultiple defaultIndex={[0]}>
                    {batch.terms.map((term) => (
                      <AccordionItem key={term.id} border="none">
                        <AccordionButton borderRadius="lg" _hover={{ bg: hoverBg }} pl={6} pr={2} py={2}>
                          <Box flex="1" textAlign="left">
                            <Text fontSize="xs" fontWeight="semibold">{term.title}</Text>
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel pt={1} pb={1} px={0}>
                          <VStack align="stretch" spacing={1}>
                            {term.assignments.map((assignment) => {
                              const isDisabled = !assignment.assignment_url;
                              const isActive = selectedAssignment?.id === assignment.id;
                              const button = (
                                <Button
                                  key={assignment.id}
                                  variant="ghost"
                                  size="sm"
                                  justifyContent="flex-start"
                                  pl={10}
                                  borderRadius="lg"
                                  bg={isActive ? hoverBg : "transparent"}
                                  _hover={{ bg: isDisabled ? "transparent" : hoverBg }}
                                  isDisabled={isDisabled}
                                  opacity={isDisabled ? 0.5 : 1}
                                  cursor={isDisabled ? "not-allowed" : "pointer"}
                                  w="100%"
                                  onClick={() => handleSelectAssignment(assignment)}
                                >
                                  <HStack spacing={2}>
                                    <Icon as={FaClipboardList} boxSize={3} />
                                    <Text fontSize="xs" noOfLines={1}>{assignment.title}</Text>
                                  </HStack>
                                </Button>
                              );
                              return isDisabled ? (
                                <Tooltip key={assignment.id} label="Not available yet" placement="right" hasArrow>
                                  <Box>{button}</Box>
                                </Tooltip>
                              ) : button;
                            })}
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Box>

        <Box bg={panelBg} borderRadius="lg" p={4} maxH="75vh" overflowY="auto">
          {!selectedAssignment && (
            <Text fontSize="sm" color={mutedText}>Select an assignment on the left to view it here.</Text>
          )}
          {selectedAssignment && (
            <>
              <Text fontWeight="bold" mb={2}>{selectedAssignment.title}</Text>
              {loading && <Text fontSize="sm" color={mutedText}>Loading…</Text>}
              {pdfUrl && (
                <Document file={pdfUrl} onLoadSuccess={({ numPages }) => setNumPages(numPages || 0)}>
                  {Array.from(new Array(numPages ?? 0), (_, index) => (
                    <Box key={index} p={2} display="flex" justifyContent="center">
                      <Page pageNumber={index + 1} width={isStacked ? 320 : 480} renderTextLayer={false} renderAnnotationLayer={false} />
                    </Box>
                  ))}
                </Document>
              )}
            </>
          )}
        </Box>
      </SimpleGrid>
    </Box>
  );
};

export default StudentAssignments;